import { useEffect, useState } from "react";
import { useAuthSession } from "~/auth/AuthSessionProvider";
import { useCurrentUser } from "~/auth/UserContext";
import { useFeatureFlag, useFeatureFlags } from "~/services/featureFlags";
import { FEATURE_FLAGS } from "~/services/featureFlags/constants";
import { AuthUserGroup } from "~/types/auth";

/**
 * Roles the /indy route guard (routes.tsx) lets through. Role is only the
 * precondition: the page itself and every MainLayout entry point (navbar tab,
 * floating panel, corner launcher, Cmd+K) also require `useIndyAccess`, which
 * layers the flags on top, so the entry points and the route can never drift.
 */
export const INDY_ROUTE_GROUPS: AuthUserGroup[] = [
  AuthUserGroup.ADMIN,
  AuthUserGroup.MEDICAL_ASSISTANT,
  AuthUserGroup.HISTORIAN,
  AuthUserGroup.DOCTOR,
];

const INDY_STAFF_GROUPS: AuthUserGroup[] = [
  AuthUserGroup.ADMIN,
  AuthUserGroup.MEDICAL_ASSISTANT,
  AuthUserGroup.HISTORIAN,
];

export interface IndyAccess {
  canUseIndy: boolean;
  /** Inputs still resolving; don't redirect or decide while true. */
  loading: boolean;
}

/**
 * Whether the signed-in user gets Indy's entry points. Staff need
 * ENABLED_CHATBOT, as before. Doctors also need the physician cohort flag on
 * their User.id. This is UX only: the AgentChat backend admits doctors against
 * the same flag row (services/agent_chat/admission.py) and is the boundary.
 */
export function useIndyAccess(): IndyAccess {
  const { userGroup } = useAuthSession();
  const isDoctor = userGroup === AuthUserGroup.DOCTOR;
  const chatbot = useFeatureFlag(FEATURE_FLAGS.ENABLED_CHATBOT);
  const physician = usePhysicianCohort(isDoctor);

  if (userGroup === null) {
    return { canUseIndy: false, loading: true };
  }
  if (INDY_STAFF_GROUPS.includes(userGroup)) {
    return { canUseIndy: chatbot.isEnabled, loading: chatbot.loading };
  }
  if (isDoctor) {
    return {
      canUseIndy: chatbot.isEnabled && physician.isEnabled,
      loading: chatbot.loading || physician.loading,
    };
  }
  return { canUseIndy: false, loading: false };
}

/**
 * The cohort flag targets User.id, which resolves after the session.
 * `useFeatureFlag` reports a stale `false` for a render once the id arrives,
 * which would bounce a cohort doctor's /indy deep link home, so the answer is
 * keyed by the id it was evaluated for.
 */
function usePhysicianCohort(isDoctor: boolean): {
  isEnabled: boolean;
  loading: boolean;
} {
  const { user, loading: userLoading } = useCurrentUser();
  const { isFeatureEnabled, loading: flagsLoading } = useFeatureFlags();
  const userId = user?.id ?? null;
  const [answer, setAnswer] = useState<{
    userId: string;
    enabled: boolean;
  } | null>(null);

  useEffect(() => {
    if (!isDoctor || !userId || flagsLoading) return;
    let live = true;
    isFeatureEnabled(FEATURE_FLAGS.ENABLED_INDY_PHYSICIAN).then(
      (enabled) => {
        if (live) setAnswer({ userId, enabled });
      },
      () => {
        if (live) setAnswer({ userId, enabled: false });
      },
    );
    return () => {
      live = false;
    };
  }, [isDoctor, userId, flagsLoading, isFeatureEnabled]);

  if (!isDoctor) return { isEnabled: false, loading: false };
  // A doctor without a User row can't be in a User.id cohort.
  if (!userId) return { isEnabled: false, loading: userLoading };
  const current = answer?.userId === userId ? answer : null;
  return { isEnabled: current?.enabled ?? false, loading: current === null };
}
